/**
 * Localisation — the public API (docs/Localization.md).
 *
 *   const t = useT();                       // React: re-renders when the language changes
 *   t('menu.sp.label')                      // "Single Player" / "Einzelspieler"
 *   t('sp.factionCount', { n: 3 })          // "3 of 6 factions" / "3 von 6 Nationen"
 *   t.num(12.5, 1), t.pct(0.6), t.locale    // "12,5", "60 %", 'de'
 *   tr('de')('hud.paused')                  // outside React, a fixed language
 *   t('…')                                  // in pure modules: the `t` export follows the current locale
 *
 * Entries are templates with `{name}` placeholders, plural forms (`{ one, other }` on `n`) or
 * wording functions (German grammar, generated prose). Keys and parameters are type-checked
 * against the English catalogue.
 */
import { useMemo, useSyncExternalStore } from 'react';
import { de } from './de';
import { type En, en } from './en';
import { getLocale, type Locale, onLocale } from './locale';
import type { ArgsOf, Msg, Plural, Val } from './types';

export type { Locale, Localized } from './locale';
export { detectLocale, getLocale, inEveryLocale, LOCALE_NAME, LOCALES, onLocale, setLocale } from './locale';

export type MsgKey = keyof En;

const DICTS: Record<Locale, Record<MsgKey, Msg>> = { en, de };

// ---- numbers -------------------------------------------------------------------------------------

const TAG: Record<Locale, string> = { en: 'en-US', de: 'de-DE' };
const numberFormats = new Map<string, Intl.NumberFormat>();
const pluralRules = new Map<Locale, Intl.PluralRules>();

/** Locale decimal number without grouping: 12.5 / 12,5. */
export function formatNumber(value: number, locale: Locale, digits = 0): string {
  const key = `${locale}:${digits}`;
  let f = numberFormats.get(key);
  if (!f) {
    f = new Intl.NumberFormat(TAG[locale], {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
      useGrouping: false,
    });
    numberFormats.set(key, f);
  }
  return f.format(value);
}

/**
 * Share 0..1 as a percentage the way this HUD prints it: "60 %" (English keeps the house style
 * with a space; German puts a no-break space between number and sign, as DIN 5008 wants).
 */
export function formatPercent(share: number, locale: Locale, digits = 0): string {
  const n = formatNumber(share * 100, locale, digits);
  return locale === 'de' ? `${n} %` : `${n} %`;
}

function plural(locale: Locale, n: number): 'one' | 'other' {
  let r = pluralRules.get(locale);
  if (!r) {
    r = new Intl.PluralRules(TAG[locale]);
    pluralRules.set(locale, r);
  }
  return r.select(n) === 'one' ? 'one' : 'other';
}

// ---- messages ------------------------------------------------------------------------------------

function fill(template: string, params: Record<string, Val> | undefined, locale: Locale): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const v = params[name];
    if (v === undefined) return whole;
    // Integers print as they are (years, ids, counts); fractions get the locale's decimal sign.
    return typeof v === 'number' && !Number.isInteger(v) ? formatNumber(v, locale, 1) : String(v);
  });
}

/**
 * German typesetting: number and unit never part at a line break ("60 %", "−3 °C", "12 Std.").
 * Applied to every German message, so translators can simply type a space.
 */
const typesetDe = (s: string): string => s.replace(/(\d) (%|°C|Std\.|Tg\.|Tsd\.|km)/g, '$1 $2');

function render(locale: Locale, key: MsgKey, params: unknown): string {
  const msg = DICTS[locale][key] ?? DICTS.en[key];
  if (msg === undefined) return key;
  const p = params as Record<string, Val> | undefined;
  const out =
    typeof msg === 'function'
      ? msg(params)
      : typeof msg === 'string'
        ? fill(msg, p, locale)
        : fill((msg as Plural)[plural(locale, Number(p?.n ?? 0))], p, locale);
  return locale === 'de' ? typesetDe(out) : out;
}

/** A translator bound to one language, with its number helpers. */
export interface Translator {
  <K extends MsgKey>(key: K, ...params: ArgsOf<En[K]>): string;
  readonly locale: Locale;
  /** Locale number with fixed decimals. */
  num(value: number, digits?: number): string;
  /** Share 0..1 as a percentage. */
  pct(share: number, digits?: number): string;
  /** A message chosen at run time (a key from a list); parameters, if any, are not filled. */
  plain(key: MsgKey): string;
}

const translators = new Map<Locale, Translator>();

/** The translator for a language (cached; stable identity per locale). */
export function tr(locale: Locale): Translator {
  let fn = translators.get(locale);
  if (!fn) {
    const f = ((key: MsgKey, params?: unknown) => render(locale, key, params)) as Translator;
    Object.assign(f, {
      locale,
      num: (value: number, digits = 0) => formatNumber(value, locale, digits),
      pct: (share: number, digits = 0) => formatPercent(share, locale, digits),
      plain: (key: MsgKey) => render(locale, key, undefined),
    });
    fn = f;
    translators.set(locale, fn);
  }
  return fn;
}

/** Translate in the current language (pure modules, event handlers, the renderer). */
export function t<K extends MsgKey>(key: K, ...params: ArgsOf<En[K]>): string {
  return render(getLocale(), key, params[0]);
}

/** The current language; re-renders the component when it changes. */
export function useLocale(): Locale {
  return useSyncExternalStore(onLocale, getLocale, getLocale);
}

/** The translator for the current language; re-renders the component when it changes. */
export function useT(): Translator {
  const locale = useLocale();
  return useMemo(() => tr(locale), [locale]);
}

/** Every key of the catalogue (tests: completeness and placeholder checks). */
export const MESSAGE_KEYS = Object.keys(en) as MsgKey[];

/** Raw entry of a language (tests). */
export const rawMessage = (locale: Locale, key: MsgKey): Msg => DICTS[locale][key];
